// Core
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

// Components
import { DataTable, RemoteTable } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import { useDispatch } from "react-redux";
import {
  openNotificationPopup,
  openSuccessAlert,
  openWarningAlert,
} from "state/application/applicationSlice";
import {
  useAddNewTariffsMutation,
  useDeleteTariffsByValidityMutation,
  useGetTariffsByValidityQuery,
  useGetTariffsValiditiesQuery,
} from "state/erse/tariffsApiSlice";
import {
  useAddNewLossesMutation,
  useDeleteLossesByYearMutation,
  useGetLossesByYearPaginationQuery,
  useGetLossesYearsQuery,
} from "state/erse/lossesApiSlice";

// Utils
import { getERSETypeLabelFromValue, getPeriodLabelFromValue } from "utils/constants/helpers";
import { voltageLevelTypes } from "utils/constants/index";
import { datetimeToTime, formatDate } from "utils/time";

const tariffsTemplate = "/docs/template_tarifas_ERSE.xlsx";
const lossesTemplate = "/docs/template_perdas.csv";

// Same voltage levels the losses upload accepts (see LossesDataTable).
const LOSSES_VOLTAGE_LEVELS = voltageLevelTypes
  .map((o) => o.label)
  .filter((l) => ["BT", "MT", "AT", "AT/RNT", "MAT"].includes(l));

function formatTariffValue(tariff, t) {
  const formatted = tariff.value.toFixed(4);
  if (tariff.type === "ENERGY") return `${formatted} ${t("tables.tariffs.units.energy")}`;
  if (tariff.type === "POWER") return `${formatted} ${t("tables.tariffs.units.power")}`;
  if (tariff.type === "CONTRACTED_POWER") {
    return tariff.ic_voltage_level.includes("BTN")
      ? `${formatted} ${t("tables.tariffs.units.contracted_power_btn")}`
      : `${formatted} ${t("tables.tariffs.units.contracted_power")}`;
  }
  return String(tariff.value);
}

/* Toolbar filter select (pattern from MeasurementsView's table filters). */
function FilterSelect({ value, onChange, allLabel, options, formatLabel }) {
  return (
    <select
      className="rec-input !w-auto !h-[36px] text-[13px] cursor-pointer"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{allLabel}</option>
      {options.map((o) => (
        <option key={o} value={o}>
          {formatLabel ? formatLabel(o) : o}
        </option>
      ))}
    </select>
  );
}

FilterSelect.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  allLabel: PropTypes.node.isRequired,
  options: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  formatLabel: PropTypes.func,
};

FilterSelect.defaultProps = {
  formatLabel: undefined,
};

const distinct = (rows, key) => [...new Set(rows.map((r) => r[key]))].filter((v) => v !== "—");

/* Client-paginated rec-tbl preview (design's admin-data table): plain
   headers (no sorting), 12 rows per page, search + toolbar filters. */
function TariffsPreviewTable({ validity }) {
  const { t } = useTranslation(["erse", "common"]);
  const { data, isFetching } = useGetTariffsByValidityQuery(validity, { skip: !validity });

  const [typeFilter, setTypeFilter] = useState("");
  const [icFilter, setIcFilter] = useState("");
  const [periodFilter, setPeriodFilter] = useState("");

  const columns = [
    { key: "type", label: t("tables.tariffs.columns.type"), sort: false },
    { key: "ic_voltage_level", label: t("tables.tariffs.columns.ic_voltage_level"), sort: false },
    { key: "ipr_voltage_level", label: t("tables.tariffs.columns.ipr_voltage_level"), sort: false },
    { key: "trimester", label: t("tables.tariffs.columns.trimester"), sort: false },
    { key: "period", label: t("tables.tariffs.columns.period"), sort: false },
    { key: "cieg_discount", label: t("tables.tariffs.columns.cieg_discount"), sort: false },
    {
      key: "value",
      label: t("tables.tariffs.columns.value"),
      sort: false,
      render: (v) => <span className="font-semibold text-ink">{v}</span>,
    },
  ];

  const rows = (data ?? []).map((tariff, i) => ({
    id: i,
    type: getERSETypeLabelFromValue(tariff.type),
    ic_voltage_level: tariff.ic_voltage_level,
    ipr_voltage_level: tariff.ipr_voltage_level || "—",
    trimester: tariff.trimester > 0 ? tariff.trimester : "—",
    period: tariff.period ? getPeriodLabelFromValue(tariff.period) : "—",
    cieg_discount: tariff.cieg_discount ? `${tariff.cieg_discount}%` : "—",
    value: formatTariffValue(tariff, t),
  }));

  const filteredRows = rows.filter(
    (r) =>
      (!typeFilter || r.type === typeFilter) &&
      (!icFilter || r.ic_voltage_level === icFilter) &&
      (!periodFilter || r.period === periodFilter)
  );

  return (
    <div style={isFetching ? { opacity: 0.65 } : undefined}>
      <DataTable
        columns={columns}
        rows={filteredRows}
        maxRows={12}
        footer
        search={t("tables.tariffs.search")}
        searchKeys={["type", "ic_voltage_level", "ipr_voltage_level", "period"]}
        filters={
          <>
            <FilterSelect
              value={typeFilter}
              onChange={setTypeFilter}
              allLabel={t("tables.tariffs.filters.all_types")}
              options={distinct(rows, "type")}
            />
            <FilterSelect
              value={icFilter}
              onChange={setIcFilter}
              allLabel={t("tables.tariffs.filters.all_ic_levels")}
              options={distinct(rows, "ic_voltage_level")}
            />
            <FilterSelect
              value={periodFilter}
              onChange={setPeriodFilter}
              allLabel={t("tables.tariffs.filters.all_periods")}
              options={distinct(rows, "period")}
            />
          </>
        }
      />
    </div>
  );
}

TariffsPreviewTable.propTypes = {
  validity: PropTypes.string.isRequired,
};

/* rec-styled server-paginated preview of the yearly loss profiles with
   date/time/voltage-level filters (server-side, as in MeasurementsView). */
function LossesPreviewTable({ year }) {
  const { t } = useTranslation(["erse"]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState(undefined);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [voltageLevel, setVoltageLevel] = useState(null);
  const limit = 12;

  useEffect(() => {
    setPage(0);
    setSort(undefined);
    setDate(null);
    setTime(null);
    setVoltageLevel(null);
  }, [year]);

  const onFilter = (setter) => (v) => {
    setPage(0);
    setter(v || null);
  };

  const { data: responseDict, isFetching } = useGetLossesByYearPaginationQuery(
    {
      year,
      limit,
      page,
      date,
      time,
      voltageLevel,
      sortColumn: sort?.column,
      sortDirection: sort?.direction,
    },
    { skip: !year }
  );

  const rows =
    responseDict?.data?.map((loss, i) => ({
      id: i,
      date: formatDate(loss.datetime_timestamp),
      time: datetimeToTime(loss.datetime_timestamp),
      voltage_level: loss.voltage_level,
      value: loss.value,
    })) ?? [];

  const columns = [
    { key: "date", label: t("tables.losses.columns.date"), sortable: true },
    { key: "time", label: t("tables.losses.columns.time"), sortable: true },
    { key: "voltage_level", label: t("tables.losses.columns.voltage_level"), sortable: true },
    {
      key: "value",
      label: t("tables.losses.columns.value"),
      align: "right",
      sortable: true,
      render: (v) => <span className="font-semibold text-ink">{v}</span>,
    },
  ];

  return (
    <RemoteTable
      columns={columns}
      rows={rows}
      totalCount={responseDict?.total_count ?? 0}
      page={page}
      limit={limit}
      onPageChange={setPage}
      sort={sort}
      onSortChange={setSort}
      loading={isFetching}
      filters={
        <>
          <FilterSelect
            value={date ?? ""}
            onChange={onFilter(setDate)}
            allLabel={t("tables.losses.filters.all_dates")}
            options={(responseDict?.distinct_dates ?? []).map((o) => o.date)}
            formatLabel={(value) => formatDate(value)}
          />
          <FilterSelect
            value={time ?? ""}
            onChange={onFilter(setTime)}
            allLabel={t("tables.losses.filters.all_times")}
            options={(responseDict?.distinct_times ?? []).map((o) => o.time)}
          />
          <FilterSelect
            value={voltageLevel ?? ""}
            onChange={onFilter(setVoltageLevel)}
            allLabel={t("tables.losses.filters.all_voltage_levels")}
            options={LOSSES_VOLTAGE_LEVELS}
          />
        </>
      }
    />
  );
}

LossesPreviewTable.propTypes = {
  year: PropTypes.string.isRequired,
};

/* Reference-dataset card (design's RefDatasetCard): icon header with
   template/upload actions, version bar with validity select + delete,
   preview table or dashed empty state. The prototype's file metadata
   (name, uploader, date) is omitted — the backend does not store it. */
function RefDatasetCard({
  icon,
  title,
  subtitle,
  templateHref,
  uploadId,
  accept,
  uploadLabel,
  uploadingLabel,
  templateLabel,
  deleteLabel,
  isUploading,
  isDeleting,
  onUpload,
  options,
  selected,
  onSelect,
  onDelete,
  emptyLabel,
  children,
}) {
  const { t } = useTranslation(["admin"]);
  const [open, setOpen] = useState(false);

  return (
    <div className="rec-card">
      <div className="rec-ref-head">
        <div className="rec-kpi-ic rec-tint-teal" style={{ width: 38, height: 38 }}>
          <Icon name={icon} fill style={{ fontSize: 20 }} />
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="text-[15.5px] font-bold text-ink">{title}</div>
          <div className="text-[12px] text-ink-3">{subtitle}</div>
        </div>
        <a className="rec-btn-ghost rec-btn-sm no-underline" href={templateHref}>
          <Icon name="download" style={{ fontSize: 16 }} />
          {templateLabel}
        </a>
        <UploadHelpButton />
        <label
          htmlFor={uploadId}
          className="rec-btn-primary rec-btn-sm"
          style={isUploading || isDeleting ? { opacity: 0.55, pointerEvents: "none" } : undefined}
        >
          <Icon name="upload" style={{ fontSize: 16 }} />
          {isUploading ? uploadingLabel : uploadLabel}
          <input id={uploadId} type="file" accept={accept} hidden onChange={onUpload} />
        </label>
      </div>

      {options.length > 0 && (
        <div
          className="rec-ref-version-bar"
          style={{
            borderBottom: selected && open ? "1px solid var(--border)" : "none",
            borderRadius: "0 0 var(--radius) var(--radius)",
          }}
        >
          <Icon name="event" style={{ fontSize: 17, color: "var(--accent)" }} />
          <select
            className="rec-input"
            style={{ width: "auto", height: 36, fontSize: 13, fontWeight: 600, borderRadius: 9 }}
            value={selected}
            onChange={(e) => onSelect(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <div className="grow" />
          {selected && (
            <button
              type="button"
              className="rec-btn-ghost rec-btn-sm"
              onClick={() => setOpen((o) => !o)}
            >
              <Icon name={open ? "visibility_off" : "table_rows"} />
              {open ? t("reference.hide_table") : t("reference.show_table")}
              <Icon
                name={open ? "expand_less" : "expand_more"}
                style={{ fontSize: 17, marginLeft: -3 }}
              />
            </button>
          )}
          <button
            type="button"
            className="rec-btn-ghost rec-btn-sm"
            style={{ color: "var(--danger)" }}
            disabled={!selected || isDeleting}
            onClick={() => onDelete(selected)}
          >
            <Icon name="delete_outline" />
            {deleteLabel}
          </button>
        </div>
      )}

      {selected ? (
        open && children
      ) : (
        <div
          className="flex flex-col items-center gap-2 text-ink-3"
          style={{
            padding: "38px 0",
            margin: "var(--pad-card)",
            border: "1px dashed var(--border-strong)",
            borderRadius: "var(--radius)",
          }}
        >
          <Icon name="upload_file" style={{ fontSize: 30, opacity: 0.5 }} />
          <span className="text-[12.5px]">{emptyLabel}</span>
        </div>
      )}
    </div>
  );
}

RefDatasetCard.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  subtitle: PropTypes.node.isRequired,
  templateHref: PropTypes.string.isRequired,
  uploadId: PropTypes.string.isRequired,
  accept: PropTypes.string.isRequired,
  uploadLabel: PropTypes.node.isRequired,
  uploadingLabel: PropTypes.node.isRequired,
  templateLabel: PropTypes.node.isRequired,
  deleteLabel: PropTypes.node.isRequired,
  isUploading: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
    })
  ).isRequired,
  selected: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  emptyLabel: PropTypes.node.isRequired,
  children: PropTypes.node,
};

RefDatasetCard.defaultProps = {
  children: null,
};

/* ── Reference data (ERSE tariffs + losses) ─────────────────────────────── */
function ReferenceDataSection() {
  const dispatch = useDispatch();
  const { t } = useTranslation(["admin"]);

  const [selectedTariffValidity, setSelectedTariffValidity] = useState("");
  const [selectedLossesYear, setSelectedLossesYear] = useState("");

  const { data: tariffValidities, refetch: refetchTariffs } = useGetTariffsValiditiesQuery();
  const { data: lossesYears, refetch: refetchLosses } = useGetLossesYearsQuery();

  const [addTariffs, { isLoading: isUploadingTariffs }] = useAddNewTariffsMutation();
  const [deleteTariffs, { isLoading: isDeletingTariffs }] = useDeleteTariffsByValidityMutation();
  const [addLosses, { isLoading: isUploadingLosses }] = useAddNewLossesMutation();
  const [deleteLosses, { isLoading: isDeletingLosses }] = useDeleteLossesByYearMutation();

  useEffect(() => {
    setSelectedTariffValidity((current) => {
      if (!tariffValidities?.length) return "";
      const stillAvailable = tariffValidities.some((item) => String(item.validity) === current);
      return stillAvailable ? current : String(tariffValidities[0].validity);
    });
  }, [tariffValidities]);

  useEffect(() => {
    setSelectedLossesYear((current) => {
      if (!lossesYears?.length) return "";
      const stillAvailable = lossesYears.some((item) => String(item.year) === current);
      return stillAvailable ? current : String(lossesYears[0].year);
    });
  }, [lossesYears]);

  const tariffOptions =
    tariffValidities?.map((item) => ({
      value: String(item.validity),
      label: t("tariffs.valid_from", { validity: formatDate(item.validity) }),
    })) ?? [];
  const lossesOptions =
    lossesYears?.map((item) => ({
      value: String(item.year),
      label: t("losses.year", { year: item.year }),
    })) ?? [];

  const notifyError = (err) =>
    dispatch(openNotificationPopup({ notification: err, type: "error" }));

  const handleTariffsUpload = async (event) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    try {
      const response = await addTariffs({ file }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("tariffs.notifications.uploaded_title"),
          description: response?.message ?? t("tariffs.notifications.uploaded_ok"),
        })
      );
      refetchTariffs();
    } catch (err) {
      notifyError(err);
    } finally {
      event.target.value = "";
    }
  };

  const handleDeleteTariff = (validity) => {
    const action = async () => {
      try {
        await deleteTariffs(validity).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("tariffs.notifications.deleted_title"),
            description: t("tariffs.notifications.deleted_ok"),
          })
        );
        refetchTariffs();
      } catch (err) {
        notifyError(err);
      }
    };

    dispatch(
      openWarningAlert({
        confirmText: t("tariffs.delete_confirm"),
        confirmAction: action,
      })
    );
  };

  const handleLossesUpload = async (event) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    try {
      const response = await addLosses({ file }).unwrap();
      dispatch(
        openSuccessAlert({
          title: t("losses.notifications.uploaded_title"),
          description: response?.message ?? t("losses.notifications.uploaded_ok"),
        })
      );
      refetchLosses();
    } catch (err) {
      notifyError(err);
    } finally {
      event.target.value = "";
    }
  };

  const handleDeleteLosses = (year) => {
    const action = async () => {
      try {
        await deleteLosses(year).unwrap();
        dispatch(
          openSuccessAlert({
            title: t("losses.notifications.deleted_title"),
            description: t("losses.notifications.deleted_ok"),
          })
        );
        refetchLosses();
      } catch (err) {
        notifyError(err);
      }
    };

    dispatch(
      openWarningAlert({
        confirmText: t("losses.delete_confirm"),
        confirmAction: action,
      })
    );
  };

  return (
    <div className="flex flex-col gap-rec">
      <RefDatasetCard
        icon="receipt_long"
        title={t("tariffs.title")}
        subtitle={t("tariffs.subtitle")}
        templateHref={tariffsTemplate}
        uploadId="admin-erse-tariffs-upload"
        accept=".xlsx"
        templateLabel={t("tariffs.template")}
        uploadLabel={t("tariffs.upload")}
        uploadingLabel={t("tariffs.uploading")}
        deleteLabel={t("tariffs.delete")}
        isUploading={isUploadingTariffs}
        isDeleting={isDeletingTariffs}
        onUpload={handleTariffsUpload}
        options={tariffOptions}
        selected={selectedTariffValidity}
        onSelect={setSelectedTariffValidity}
        onDelete={handleDeleteTariff}
        emptyLabel={t("tariffs.empty")}
      >
        <TariffsPreviewTable validity={selectedTariffValidity} />
      </RefDatasetCard>

      <RefDatasetCard
        icon="trending_down"
        title={t("losses.title")}
        subtitle={t("losses.subtitle")}
        templateHref={lossesTemplate}
        uploadId="admin-erse-losses-upload"
        accept=".csv"
        templateLabel={t("losses.template")}
        uploadLabel={t("losses.upload")}
        uploadingLabel={t("losses.uploading")}
        deleteLabel={t("losses.delete")}
        isUploading={isUploadingLosses}
        isDeleting={isDeletingLosses}
        onUpload={handleLossesUpload}
        options={lossesOptions}
        selected={selectedLossesYear}
        onSelect={setSelectedLossesYear}
        onDelete={handleDeleteLosses}
        emptyLabel={t("losses.empty")}
      >
        <LossesPreviewTable year={selectedLossesYear} />
      </RefDatasetCard>
    </div>
  );
}

export default ReferenceDataSection;
